import { useState, useEffect } from "react";
import {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiHome,
  FiFile,
  FiMenu,
  FiZoomIn,
  FiZoomOut,
  FiMaximize2,
  FiChevronDown,
  FiChevronRight,
  FiMoreHorizontal,
  FiX,
  FiLoader,
  FiImage,
} from "react-icons/fi";
import ComponentPicker from "./ComponentPicker";
import { getThemeHeaderCode, getThemeFooterCode } from "../../../data/component-templates/themes";
import COMPONENT_NAMES from "../../../data/component-name-map";

// Add Page Button Component - sits on the horizontal line between pages
const AddPageButton = ({ theme, onClick, disabled }) => {
  const [isHovered, setIsHovered] = useState(false);

  if (disabled) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        width: "24px",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Add button - always visible, positioned on the horizontal line */}
      <div
        onClick={onClick}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: isHovered ? theme.buttonBg : theme.sectionBorder,
          color: isHovered ? "#fff" : theme.textMuted,
          boxShadow: isHovered ? "0 2px 6px rgba(0,0,0,0.2)" : "none",
          marginTop: "-12px",
          zIndex: 20,
          transition: "all 0.15s ease",
        }}
      >
        <FiPlus size={14} />
      </div>
    </div>
  );
};

// Add Page Modal Component
const AddPageModal = ({ theme, darkMode, onClose, onSubmit, isLoading }) => {
  const [pageName, setPageName] = useState("");
  const [pageDescription, setPageDescription] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pageName.trim()) {
      onSubmit(pageName.trim(), pageDescription.trim());
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "90%",
          maxWidth: "450px",
          backgroundColor: darkMode ? "#1f1f1f" : "#ffffff",
          borderRadius: "14px",
          border: `1px solid ${darkMode ? "#3a3a3a" : "#E5E7EB"}`,
          boxShadow: darkMode ? "0 20px 40px rgba(0,0,0,0.3)" : "0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: `1px solid ${darkMode ? "#3a3a3a" : "#e5e7eb"}`,
          }}
        >
          <h5 style={{ margin: 0, color: theme.text, fontSize: "16px", fontWeight: "600" }}>
            Add New Page
          </h5>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: theme.textMuted,
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: "20px" }}>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: theme.text,
                }}
              >
                Page Name *
              </label>
              <input
                type="text"
                value={pageName}
                onChange={(e) => setPageName(e.target.value)}
                placeholder="e.g., Services, About Us, Gallery"
                maxLength={50}
                autoFocus
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: `1px solid ${darkMode ? "#444" : "#d1d5db"}`,
                  backgroundColor: darkMode ? "#2a2a2a" : "#ffffff",
                  color: theme.text,
                  fontSize: "14px",
                  outline: "none",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: theme.text,
                }}
              >
                Page Description
              </label>
              <textarea
                value={pageDescription}
                onChange={(e) => setPageDescription(e.target.value)}
                placeholder="Describe the purpose and content of this page..."
                maxLength={200}
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: `1px solid ${darkMode ? "#444" : "#d1d5db"}`,
                  backgroundColor: darkMode ? "#2a2a2a" : "#ffffff",
                  color: theme.text,
                  fontSize: "14px",
                  outline: "none",
                  resize: "none",
                }}
              />
              <p style={{ margin: "6px 0 0", fontSize: "11px", color: theme.textMuted }}>
                AI will generate relevant sections based on your description
              </p>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              padding: "16px 20px",
              borderTop: `1px solid ${darkMode ? "#3a3a3a" : "#e5e7eb"}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: `1px solid ${darkMode ? "#444" : "#d1d5db"}`,
                backgroundColor: "transparent",
                color: theme.text,
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!pageName.trim() || isLoading}
              style={{
                padding: "8px 20px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: !pageName.trim() || isLoading ? "#666" : theme.buttonBg,
                color: "#fff",
                fontSize: "14px",
                cursor: !pageName.trim() || isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {isLoading ? (
                <>
                  <FiLoader className="spinner-border spinner-border-sm" size={14} />
                  Generating...
                </>
              ) : (
                <>
                  <FiPlus size={14} />
                  Add Page
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Insert Section Button Component - floating between sections
const InsertSectionButton = ({ theme, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        position: "relative",
        height: "0",
        display: "flex",
        justifyContent: "center",
        zIndex: 10,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Insert button - only visible on hover */}
      {isHovered && (
        <div
          onClick={onClick}
          style={{
            position: "absolute",
            top: "-9px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "18px",
            height: "18px",
            borderRadius: "50%",
            backgroundColor: theme.sectionBorder,
            color: theme.textMuted,
            boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          }}
        >
          <FiPlus size={10} />
        </div>
      )}
      {/* Hover detection area - always present */}
      <div
        style={{
          position: "absolute",
          top: "-8px",
          left: "0",
          right: "0",
          height: "16px",
          cursor: "pointer",
        }}
        onClick={onClick}
      />
    </div>
  );
};

// Page Card Component
const PageCard = ({
  page,
  pageIndex,
  isHome,
  theme,
  darkMode,
  collapsedPages,
  togglePageCollapse,
  hoveredSection,
  setHoveredSection,
  draggedSection,
  dragOverSection,
  handleDragStart,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleDragEnd,
  getSectionTitle,
  replaceSection,
  deleteSection,
  addSection,
  deletePage,
  updatePageName,
  sitemap,
  viewMode,
}) => {
  const isCollapsed = collapsedPages[pageIndex];
  const isVisual = viewMode === "visual";

  return (
    <div
      className="page-card"
      style={{
        backgroundColor: theme.cardBg,
        border: `1px solid ${theme.cardBorder}`,
        borderRadius: "12px",
        overflow: "visible",
        boxShadow: darkMode ? "none" : "0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)",
        minWidth: isVisual ? (isHome ? "320px" : "280px") : (isHome ? "280px" : "220px"),
        maxWidth: isVisual ? (isHome ? "360px" : "320px") : (isHome ? "320px" : "240px"),
        transition: "min-width 0.3s ease, max-width 0.3s ease",
      }}
    >
      {/* Page Header */}
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{
          backgroundColor: theme.headerBg,
          borderBottom: isCollapsed ? "none" : `1px solid ${theme.cardBorder}`,
          borderRadius: isCollapsed ? "12px" : "12px 12px 0 0",
          gap: "8px",
        }}
      >
        <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
          {isHome ? (
            <FiHome size={14} style={{ color: theme.buttonBg, flexShrink: 0 }} />
          ) : (
            <FiFile size={14} style={{ color: theme.textMuted, flexShrink: 0 }} />
          )}
          <input
            type="text"
            className="form-control form-control-sm border-0 bg-transparent p-0"
            style={{
              fontWeight: "600",
              fontSize: "13px",
              color: theme.text,
              minWidth: 0,
            }}
            value={page.name}
            onChange={(e) => updatePageName(pageIndex, e.target.value)}
          />
        </div>
        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm p-1"
            style={{
              color: theme.iconColor,
              backgroundColor: "transparent",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => togglePageCollapse(pageIndex)}
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? <FiChevronRight size={12} /> : <FiChevronDown size={12} />}
          </button>
          {!isHome && (
            <div className="dropdown">
              <button
                className="btn btn-sm p-1"
                style={{
                  color: theme.iconColor,
                  backgroundColor: "transparent",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                data-bs-toggle="dropdown"
              >
                <FiMoreHorizontal size={12} />
              </button>
              <ul className={`dropdown-menu dropdown-menu-end ${darkMode ? 'dropdown-menu-dark' : ''}`}>
                <li>
                  <button
                    className="dropdown-item d-flex align-items-center gap-2 text-danger"
                    onClick={() => deletePage(pageIndex)}
                  >
                    <FiTrash2 size={14} /> Delete Page
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Page Body - Sections */}
      {!isCollapsed && (
        <div className="p-2" style={{ backgroundColor: theme.cardBg, borderRadius: "0 0 12px 12px" }}>
          {page.sections?.map((section, sectionIndex) => {
            const isDragging =
              draggedSection?.pageIndex === pageIndex &&
              draggedSection?.sectionIndex === sectionIndex;
            const isDragOver =
              dragOverSection?.pageIndex === pageIndex &&
              dragOverSection?.sectionIndex === sectionIndex;
            const isHovered =
              hoveredSection?.pageIndex === pageIndex &&
              hoveredSection?.sectionIndex === sectionIndex;

            return (
              <div key={sectionIndex}>
                {/* Section Block */}
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, pageIndex, sectionIndex)}
                  onDragOver={(e) => handleDragOver(e, pageIndex, sectionIndex)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, pageIndex, sectionIndex)}
                  onDragEnd={handleDragEnd}
                  onMouseEnter={() => setHoveredSection({ pageIndex, sectionIndex })}
                  onMouseLeave={() => setHoveredSection(null)}
                  style={{
                    backgroundColor: isVisual ? "transparent" : theme.sectionBg,
                    border: isVisual
                      ? (isDragOver ? `2px solid ${theme.buttonBg}` : "none")
                      : `1px solid ${isDragOver ? theme.buttonBg : theme.sectionBorder}`,
                    borderRadius: isVisual ? "0" : "8px",
                    padding: isVisual ? "0" : "8px 10px",
                    marginBottom: isVisual ? "0" : "6px",
                    cursor: "grab",
                    opacity: isDragging ? 0.5 : 1,
                    transition: "all 0.25s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {isVisual ? (
                    /* === VISUAL PREVIEW MODE === */
                    <div style={{ position: "relative", lineHeight: 0 }}>
                      <img
                        src={`/component-previews/${section.component_code}.png`}
                        alt={section.component_code}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      {/* Fallback when image fails */}
                      <div
                        style={{
                          display: "none",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "80px",
                          backgroundColor: theme.sectionBg,
                          color: theme.textMuted,
                          fontSize: "11px",
                        }}
                      >
                        <FiImage size={16} style={{ marginRight: "6px", opacity: 0.5 }} />
                        {getSectionTitle(section.component_code)}
                      </div>
                      {/* Floating label badge */}
                      <span
                        style={{
                          position: "absolute",
                          top: "4px",
                          left: "4px",
                          fontSize: "9px",
                          fontWeight: "600",
                          color: "#fff",
                          backgroundColor: "rgba(124, 58, 237, 0.85)",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          letterSpacing: "0.3px",
                          fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                          lineHeight: "1.4",
                          pointerEvents: "none",
                        }}
                      >
                        {getSectionTitle(section.component_code)}
                      </span>
                      {/* Floating action buttons on hover */}
                      {isHovered && (
                        <div
                          style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            display: "flex",
                            gap: "2px",
                          }}
                        >
                          <button
                            className="btn btn-sm p-0"
                            style={{
                              width: "22px",
                              height: "22px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              backgroundColor: "rgba(124, 58, 237, 0.85)",
                              border: "none",
                              borderRadius: "4px",
                              lineHeight: 1,
                            }}
                            title="Replace"
                            onClick={(e) => {
                              e.stopPropagation();
                              replaceSection(pageIndex, sectionIndex);
                            }}
                          >
                            <FiEdit2 size={11} />
                          </button>
                          <button
                            className="btn btn-sm p-0"
                            style={{
                              width: "22px",
                              height: "22px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              backgroundColor: "rgba(239, 68, 68, 0.85)",
                              border: "none",
                              borderRadius: "4px",
                              lineHeight: 1,
                            }}
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSection(pageIndex, sectionIndex);
                            }}
                          >
                            <FiTrash2 size={11} />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* === TEXT MODE (original) === */
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "12px",
                            color: theme.buttonBg,
                            marginBottom: "2px",
                          }}
                        >
                          {getSectionTitle(section.component_code)}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: theme.textMuted,
                            lineHeight: "1.3",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {section.purpose}
                        </div>
                      </div>
                      {isHovered && (
                        <div className="d-flex align-items-center gap-1" style={{ marginLeft: "4px" }}>
                          <button
                            className="btn btn-sm p-1"
                            style={{
                              color: theme.buttonBg,
                              backgroundColor: "transparent",
                              border: "none",
                            }}
                            title="Replace"
                            onClick={(e) => {
                              e.stopPropagation();
                              replaceSection(pageIndex, sectionIndex);
                            }}
                          >
                            <FiEdit2 size={12} />
                          </button>
                          <button
                            className="btn btn-sm p-1"
                            style={{
                              color: "#ef4444",
                              backgroundColor: "transparent",
                              border: "none",
                            }}
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSection(pageIndex, sectionIndex);
                            }}
                          >
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Insert Section Button - between sections */}
                <InsertSectionButton
                  theme={theme}
                  onClick={() => addSection(pageIndex, sectionIndex)}
                />
              </div>
            );
          })}

          {/* Add Section Button */}
          <button
            className="btn btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
            style={{
              borderStyle: "dashed",
              padding: "6px",
              fontSize: "11px",
              border: `1px dashed ${theme.cardBorder}`,
              color: theme.textMuted,
              backgroundColor: "transparent",
              marginTop: "4px",
            }}
            onClick={() => addSection(pageIndex)}
          >
            <FiPlus size={12} /> Add Section
          </button>
        </div>
      )}
    </div>
  );
};

const SitemapCanvas = ({ sitemap, onUpdate, niche, darkMode = false, showZoomControls = false, projectId, onGeneratePage, viewMode = "text", projectTheme = "classic" }) => {
  const [showComponentPicker, setShowComponentPicker] = useState(false);
  const [pickerContext, setPickerContext] = useState(null);
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverSection, setDragOverSection] = useState(null);
  const [hoveredSection, setHoveredSection] = useState(null);
  const [collapsedPages, setCollapsedPages] = useState({});
  const [zoom, setZoom] = useState(100);
  const [showAddPageModal, setShowAddPageModal] = useState(false);
  const [addPagePosition, setAddPagePosition] = useState(null); // Position to insert new page
  const [isGeneratingPage, setIsGeneratingPage] = useState(false);

  // Max pages limit
  const MAX_PAGES = 7;
  const canAddMorePages = sitemap?.pages?.length < MAX_PAGES;

  // Theme colors
  const theme = darkMode ? {
    bg: "#1a1a1a",
    cardBg: "#222",
    cardBorder: "#333",
    headerBg: "#1f1f1f",
    sectionBg: "#2a2a2a",
    sectionBorder: "#3a3a3a",
    sectionHover: "#333",
    text: "#f5f5f5",
    textMuted: "#888",
    iconColor: "#888",
    buttonBg: "#7c3aed",
    buttonBorder: "#7c3aed",
    lineBg: "#444",
  } : {
    bg: "#F9FAFB",
    cardBg: "#FFFFFF",
    cardBorder: "#E5E7EB",
    headerBg: "#FAFBFC",
    sectionBg: "#F9FAFB",
    sectionBorder: "#E5E7EB",
    sectionHover: "#F3F4F6",
    text: "#1F2937",
    textMuted: "#6B7280",
    iconColor: "#9CA3AF",
    buttonBg: "#7C3AED",
    buttonBorder: "#7C3AED",
    lineBg: "#D1D5DB",
  };

  // Section drag handlers
  const handleDragStart = (e, pageIndex, sectionIndex) => {
    setDraggedSection({ pageIndex, sectionIndex });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, pageIndex, sectionIndex) => {
    e.preventDefault();
    if (
      draggedSection &&
      (draggedSection.pageIndex !== pageIndex || draggedSection.sectionIndex !== sectionIndex)
    ) {
      setDragOverSection({ pageIndex, sectionIndex });
    }
  };

  const handleDragLeave = () => {
    setDragOverSection(null);
  };

  const handleDrop = (e, targetPageIndex, targetSectionIndex) => {
    e.preventDefault();
    if (!draggedSection) return;

    const sourcePageIndex = draggedSection.pageIndex;
    const sourceSectionIndex = draggedSection.sectionIndex;
    const movedSection = { ...sitemap.pages[sourcePageIndex].sections[sourceSectionIndex] };

    const newSitemap = {
      ...sitemap,
      pages: sitemap.pages.map((page, pageIdx) => {
        let newSections = [...page.sections];

        if (pageIdx === sourcePageIndex) {
          newSections = newSections.filter((_, sIdx) => sIdx !== sourceSectionIndex);
        }

        let adjustedTargetIndex = targetSectionIndex;
        if (sourcePageIndex === targetPageIndex && sourceSectionIndex < targetSectionIndex) {
          adjustedTargetIndex = targetSectionIndex - 1;
        }

        if (pageIdx === targetPageIndex) {
          newSections.splice(adjustedTargetIndex, 0, movedSection);
        }

        return { ...page, sections: newSections };
      }),
    };

    onUpdate(newSitemap);
    setDraggedSection(null);
    setDragOverSection(null);
  };

  const handleDragEnd = () => {
    setDraggedSection(null);
    setDragOverSection(null);
  };

  const addSection = (pageIndex, afterIndex = -1) => {
    setPickerContext({ type: "add", pageIndex, afterIndex });
    setShowComponentPicker(true);
  };

  const replaceSection = (pageIndex, sectionIndex) => {
    setPickerContext({ type: "replace", pageIndex, sectionIndex });
    setShowComponentPicker(true);
  };

  const deleteSection = (pageIndex, sectionIndex) => {
    const newSitemap = {
      ...sitemap,
      pages: sitemap.pages.map((page, idx) =>
        idx === pageIndex
          ? { ...page, sections: page.sections.filter((_, sIdx) => sIdx !== sectionIndex) }
          : page
      ),
    };
    onUpdate(newSitemap);
  };

  const handleComponentSelect = (componentCode, purpose) => {
    if (!pickerContext) return;

    const newSection = { component_code: componentCode, purpose };

    const newSitemap = {
      ...sitemap,
      pages: sitemap.pages.map((page, idx) => {
        if (idx !== pickerContext.pageIndex) return page;

        if (pickerContext.type === "add") {
          const insertIndex =
            pickerContext.afterIndex >= 0
              ? pickerContext.afterIndex + 1
              : page.sections.length;
          const newSections = [...page.sections];
          newSections.splice(insertIndex, 0, newSection);
          return { ...page, sections: newSections };
        } else if (pickerContext.type === "replace") {
          const newSections = page.sections.map((s, sIdx) =>
            sIdx === pickerContext.sectionIndex ? newSection : s
          );
          return { ...page, sections: newSections };
        }
        return page;
      }),
    };

    onUpdate(newSitemap);
    setShowComponentPicker(false);
    setPickerContext(null);
  };

  const deletePage = (pageIndex) => {
    if (sitemap.pages.length <= 1) return;
    const newSitemap = {
      ...sitemap,
      pages: sitemap.pages.filter((_, idx) => idx !== pageIndex),
    };
    onUpdate(newSitemap);
  };

  const updatePageName = (pageIndex, newName) => {
    const newSitemap = {
      ...sitemap,
      pages: sitemap.pages.map((page, idx) =>
        idx === pageIndex ? { ...page, name: newName } : page
      ),
    };
    onUpdate(newSitemap);
  };

  const togglePageCollapse = (pageIndex) => {
    setCollapsedPages((prev) => ({
      ...prev,
      [pageIndex]: !prev[pageIndex],
    }));
  };

  // Open add page modal at specified position
  const openAddPageModal = (position) => {
    setAddPagePosition(position);
    setShowAddPageModal(true);
  };

  // Handle adding a new page
  const handleAddPage = async (pageName, pageDescription) => {
    if (!canAddMorePages) return;

    setIsGeneratingPage(true);

    try {
      // If parent component provides onGeneratePage callback, use it
      if (onGeneratePage) {
        const newPage = await onGeneratePage(pageName, pageDescription);
        if (newPage) {
          // Insert the new page at the specified position
          const newPages = [...sitemap.pages];
          const insertIndex = addPagePosition !== null ? addPagePosition + 1 : newPages.length;
          newPages.splice(insertIndex, 0, newPage);

          onUpdate({
            ...sitemap,
            pages: newPages,
          });
        }
      } else {
        // Fallback: create a basic page structure
        const pageId = pageName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        const newPage = {
          id: pageId,
          name: pageName,
          route: '/' + pageId,
          sections: [
            { component_code: getThemeHeaderCode(projectTheme), purpose: 'Navigation header' },
            { component_code: 'HERO_02', purpose: `${pageName} page hero section` },
            { component_code: 'CONTENT_01', purpose: `Main content for ${pageName}` },
            { component_code: 'CTA_01', purpose: 'Call to action' },
            { component_code: getThemeFooterCode(projectTheme), purpose: 'Footer' }
          ]
        };

        const newPages = [...sitemap.pages];
        const insertIndex = addPagePosition !== null ? addPagePosition + 1 : newPages.length;
        newPages.splice(insertIndex, 0, newPage);

        onUpdate({
          ...sitemap,
          pages: newPages,
        });
      }

      setShowAddPageModal(false);
      setAddPagePosition(null);
    } catch (error) {
      console.error('Failed to add page:', error);
    } finally {
      setIsGeneratingPage(false);
    }
  };

  const getSectionTitle = (componentCode) => {
    if (COMPONENT_NAMES[componentCode]) return COMPONENT_NAMES[componentCode];
    // Fallback: extract category from code
    const parts = componentCode.split("_");
    const category = parts.length >= 3 ? parts[1] : parts[0];
    return category.charAt(0) + category.slice(1).toLowerCase() + " Section";
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(150, prev + 10));
  const handleZoomOut = () => setZoom((prev) => Math.max(50, prev - 10));
  const handleFitScreen = () => setZoom(100);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        handleZoomIn();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleFitScreen();
      }
    };

    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) handleZoomIn();
        else if (e.deltaY > 0) handleZoomOut();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [zoom]);

  const homePage = sitemap?.pages?.[0];
  const childPages = sitemap?.pages?.slice(1) || [];

  const commonProps = {
    theme,
    darkMode,
    collapsedPages,
    togglePageCollapse,
    hoveredSection,
    setHoveredSection,
    draggedSection,
    dragOverSection,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    getSectionTitle,
    replaceSection,
    deleteSection,
    addSection,
    deletePage,
    updatePageName,
    sitemap,
    viewMode,
  };

  return (
    <div
      className="sitemap-canvas"
      style={{
        width: "100%",
        height: darkMode ? "calc(100vh - 60px)" : "calc(100vh - 64px)",
        backgroundColor: darkMode ? theme.bg : "transparent",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div
        className="sitemap-scroll-container"
        style={{
          width: "100%",
          height: "100%",
          overflow: "auto",
          padding: "40px 20px",
          paddingBottom: "120px",
        }}
      >
        <div
          style={{
            transform: `scale(${zoom / 100})`,
            transformOrigin: "top center",
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Tree Structure */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>

            {/* Home Page (Root) */}
            {homePage && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <PageCard
                  page={homePage}
                  pageIndex={0}
                  isHome={true}
                  {...commonProps}
                />

                {/* Add First Child Page Button (when no child pages exist) */}
                {childPages.length === 0 && canAddMorePages && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div
                      style={{
                        width: "2px",
                        height: "30px",
                        backgroundColor: theme.lineBg,
                      }}
                    />
                    <button
                      onClick={() => openAddPageModal(0)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 16px",
                        borderRadius: "10px",
                        border: `1px dashed ${theme.cardBorder}`,
                        backgroundColor: "transparent",
                        color: theme.textMuted,
                        fontSize: "13px",
                        cursor: "pointer",
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: "500",
                      }}
                    >
                      <FiPlus size={14} /> Add Page
                    </button>
                  </div>
                )}

                {/* Connector Line from Home to Children */}
                {childPages.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {/* Vertical line down from home */}
                    <div
                      style={{
                        width: "2px",
                        height: "40px",
                        backgroundColor: theme.lineBg,
                      }}
                    />

                    {/* Child Pages Row with horizontal connector */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        position: "relative",
                        gap: "16px",
                      }}
                    >
                      {/* Horizontal line spanning full width */}
                      <div
                        style={{
                          position: "absolute",
                          top: "0",
                          left: "0",
                          right: "0",
                          height: "2px",
                          backgroundColor: theme.lineBg,
                        }}
                      />

                      {/* Add Page Button at the start */}
                      {canAddMorePages && (
                        <AddPageButton
                          theme={theme}
                          onClick={() => openAddPageModal(0)}
                          disabled={!canAddMorePages}
                        />
                      )}

                      {childPages.map((page, index) => (
                        <div
                          key={page.id || index + 1}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "16px",
                          }}
                        >
                          {/* Page Card with connector */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            {/* Vertical line down to each child */}
                            <div
                              style={{
                                width: "2px",
                                height: "30px",
                                backgroundColor: theme.lineBg,
                              }}
                            />
                            <PageCard
                              page={page}
                              pageIndex={index + 1}
                              isHome={false}
                              {...commonProps}
                            />
                          </div>

                          {/* Add Page Button after each page */}
                          {canAddMorePages && (
                            <AddPageButton
                              theme={theme}
                              onClick={() => openAddPageModal(index + 1)}
                              disabled={!canAddMorePages}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Zoom Controls */}
      {showZoomControls && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: darkMode ? "#2a2a2a" : "#FFFFFF",
            border: `1px solid ${darkMode ? "#3a3a3a" : "#E5E7EB"}`,
            borderRadius: "10px",
            padding: "8px 12px",
            boxShadow: darkMode ? "0 4px 12px rgba(0,0,0,0.15)" : "0 4px 8px -2px rgba(16, 24, 40, 0.1), 0 2px 4px -2px rgba(16, 24, 40, 0.06)",
            zIndex: 100,
          }}
        >
          <button
            onClick={handleZoomOut}
            style={{
              background: "transparent",
              border: "none",
              color: theme.text,
              cursor: "pointer",
              padding: "4px 8px",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
            }}
          >
            <FiZoomOut size={18} />
          </button>
          <span style={{ fontSize: "14px", color: theme.text, minWidth: "60px", textAlign: "center", fontWeight: "500" }}>
            {zoom}%
          </span>
          <button
            onClick={handleZoomIn}
            style={{
              background: "transparent",
              border: "none",
              color: theme.text,
              cursor: "pointer",
              padding: "4px 8px",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
            }}
          >
            <FiZoomIn size={18} />
          </button>
          <div style={{ width: "1px", height: "20px", backgroundColor: theme.cardBorder, margin: "0 4px" }}></div>
          <button
            onClick={handleFitScreen}
            style={{
              background: "transparent",
              border: "none",
              color: theme.text,
              cursor: "pointer",
              padding: "4px 8px",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
            }}
          >
            <FiMaximize2 size={18} />
          </button>
        </div>
      )}

      {/* Component Picker Modal */}
      {showComponentPicker && (
        <ComponentPicker
          onSelect={handleComponentSelect}
          onClose={() => {
            setShowComponentPicker(false);
            setPickerContext(null);
          }}
          niche={niche}
          darkMode={darkMode}
          currentComponentCode={
            pickerContext?.type === "replace" && pickerContext?.pageIndex != null && pickerContext?.sectionIndex != null
              ? sitemap.pages[pickerContext.pageIndex]?.sections[pickerContext.sectionIndex]?.component_code
              : null
          }
          projectTheme={projectTheme}
        />
      )}

      {/* Add Page Modal */}
      {showAddPageModal && (
        <AddPageModal
          theme={theme}
          darkMode={darkMode}
          onClose={() => {
            setShowAddPageModal(false);
            setAddPagePosition(null);
          }}
          onSubmit={handleAddPage}
          isLoading={isGeneratingPage}
        />
      )}
    </div>
  );
};

export default SitemapCanvas;
